import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  BarChart3,
  ArrowRight,
  CheckCircle2
} from "lucide-react";
import axios from "axios";
import { TIERS, formatReferralRange, getTierColorClass } from "@/config/tiers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    document.title = "GeniusList Partner Program | Earn Up to 45% Commission";
  }, []);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data) {
          navigate('/dashboard', { replace: true });
        }
      } catch {
        // Not authenticated, show landing page
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    navigate('/login');
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Real-time Tracking",
      description: "Monitor your referrals and earnings as they happen with live updates"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Referral Management",
      description: "Track every signup, conversion, and subscription in one dashboard"
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Commission Calculator",
      description: "Project your earnings with our interactive commission calculator"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Advanced Analytics",
      description: "Deep dive into your performance with detailed charts and insights"
    }
  ];

  // Generate tiers display data from shared config
  const tiers = TIERS.map((tier) => ({
    name: tier.name,
    rate: `${tier.commissionRate}%`,
    referrals: formatReferralRange(tier),
    color: getTierColorClass(tier.id),
  }));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_trackr-genius/artifacts/eb8bd9q1_Black%20Modern%20A%20letter%20Logo.png" alt="GeniusList" className="w-12 h-12 object-contain" />
            </div>
            <span className="text-xl font-bold text-slate-900">GeniusList</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/admin/login')}
              variant="outline"
              data-testid="header-admin-login-btn"
              className="rounded-full px-6 border-slate-300 text-slate-600 hover:bg-slate-100"
            >
              Admin Login
            </Button>
            <Button 
              onClick={handleLogin}
              data-testid="header-login-btn"
              className="gradient-btn text-white rounded-full px-6"
            >
              Partner Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Grow Your Income with{" "}
              <span className="gradient-text">GeniusList</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed">
              Join our partner program and earn up to 40% commission on every referral. 
              Track your earnings, access marketing assets, and scale your affiliate business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleLogin}
                data-testid="hero-get-started-btn"
                className="gradient-btn text-white rounded-full px-8 py-6 text-lg"
              >
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                data-testid="hero-learn-more-btn"
                className="rounded-full px-8 py-6 text-lg border-slate-200"
              >
                Learn More
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "4", label: "Commission Tiers" },
              { value: "5,000+", label: "Active Partners" },
              { value: "40%", label: "Max Commission" },
              { value: "24/7", label: "Support" },
            ].map((stat, idx) => (
              <Card key={idx} className="bg-white border-slate-100 shadow-sm">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold gradient-text">{stat.value}</p>
                  <p className="text-slate-500 mt-1">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our partner dashboard gives you all the tools to track, optimize, and grow your affiliate business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-[#F8FAFC] border-slate-100 card-hover">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-500 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tier Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Commission Tiers
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Earn more as you grow. Our tiered commission structure rewards your success.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier, idx) => (
              <Card key={idx} className="bg-white border-slate-100 card-hover overflow-hidden">
                <CardContent className="p-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${tier.color} mb-4`}>
                    {tier.name}
                  </span>
                  <p className="text-4xl font-bold text-slate-900 mb-2">{tier.rate}</p>
                  <p className="text-slate-500 text-sm">Commission Rate</p>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">{tier.referrals}</span> referrals
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Why Partner with GeniusList?
              </h2>
              <div className="space-y-4">
                {[
                  "Competitive commission rates up to 40%",
                  "Real-time tracking and analytics",
                  "Professional marketing assets",
                  "Dedicated partner support",
                  "Monthly payouts via PayPal or bank transfer",
                  "No minimum payout threshold"
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-600">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button 
                onClick={handleLogin}
                data-testid="benefits-join-btn"
                className="gradient-btn text-white rounded-full px-8 mt-8"
              >
                Join Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl p-8">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600" 
                  alt="Dashboard Preview"
                  className="rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-[#6B46C1] to-[#3B82F6] border-0 overflow-hidden">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Start Earning?
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Join thousands of partners already earning with GeniusList. 
                Sign up today and start tracking your success.
              </p>
              <Button 
                onClick={handleLogin}
                data-testid="cta-get-started-btn"
                className="bg-white text-purple-600 hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium"
              >
                Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_trackr-genius/artifacts/eb8bd9q1_Black%20Modern%20A%20letter%20Logo.png" alt="GeniusList" className="w-10 h-10 object-contain" />
            </div>
            <span className="text-lg font-bold text-slate-900">GeniusList</span>
          </div>
          <p className="text-slate-500 text-sm">
            © 2025 GeniusList. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
